import { TextField } from '@mui/material';
import { getLicenseType } from '@shared/constants/organization/LicenseUtils';
import { DefaultOrgName } from '@shared/constants/trials/FallbackEmailTemplate';
import { publish } from '@shared/hooks/helpers/EventCallback';
import { useAlertsStore } from '@shared/hooks/state/AlertsContainer';
import { useOrganizationStore } from '@shared/hooks/state/OrganizationContainer';
import { LicenseKeyCheckResponse } from '@shared/models/organizations/LicenseKeyCheckResponse';
import OrganizationModel from '@shared/models/organizations/OrganizationModel';
import IResult from '@shared/models/result/IResult';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import ContentDialog from '../core/dialog/ContentDialog';
import { SubSectionContent } from '../core/layouts/CommonLayoutStyles';
import { ErrorText } from '../core/text/FormsTypography';

interface IActivateLicenseDialogProps {
    open: boolean;
    onCancel: () => void;
    organizationId?: string;
}

enum LicenseActivationSteps {
    LicenseKey = 0,
    OrganizationDetails = 1,
}

export default function ActivateLicenseDialog({ open, onCancel, organizationId }: IActivateLicenseDialogProps) {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [licenseKey, setLicenseKey] = useState<string>('');
    const [orgName, setOrgName] = useState<string>('');
    const [orgDescription, setOrgDescription] = useState<string>('');
    const [currentStep, setCurrentStep] = useState<LicenseActivationSteps>(LicenseActivationSteps.LicenseKey);

    const description = organizationId
        ? t('common:license.activate.description')
        : t('common:license.activate.descriptionLong');

    /** Activation Logic */
    const processResult = (result: IResult<OrganizationModel>) => {
        if (result.resultType == 'Ok' && result.data) {
            // navigate to activate license page
            navigate(`/org/${result.data.id}/license`, { replace: true });

            // show success message
            const licenseType = getLicenseType(result.data);
            useAlertsStore
                .getState()
                .showSuccess(
                    t('common:license.activate.success', { license: t(`common:billing.${licenseType}.titleLong`) })
                );
            publish('license-trial-started');

            // remove dialog
            onDismiss();
        } else {
            setErrorMessage(t('common:license.activate.invalid.key'));
        }
    };

    // free user is upgrading to a licensed organization
    const activateNewOrg = async (name: string, description: string) => {
        // validation
        if (name == '') {
            setErrorMessage(t('common:license.activate.invalid.empty.name'));
            return;
        }

        // submit
        const result = await useOrganizationStore.getState().activateNewOrgLicenseKey({
            name: name,
            description: description,
            key: licenseKey,
        });

        processResult(result);
    };

    // existing org is activating a license key
    const activateExistingOrg = async () => {
        // permission check
        if (!organizationId) {
            useAlertsStore.getState().showError(t('common:organization.error'));
            onDismiss();
            return;
        }

        // submit
        const result = await useOrganizationStore
            .getState()
            .activateExistingOrgLicenseKey(organizationId, { key: licenseKey });

        processResult(result);
    };

    // custom activation logic based on user type
    const onActivate = async () => {
        if (currentStep === LicenseActivationSteps.LicenseKey) {
            // validate license key
            if (licenseKey === '') {
                setErrorMessage(t('common:license.activate.invalid.empty.key'));
                return;
            }

            let validateResult: IResult<LicenseKeyCheckResponse> | null = null;
            if (organizationId) {
                validateResult = await useOrganizationStore
                    .getState()
                    .validateLicenseKeyForOrganization(organizationId, licenseKey);
            } else {
                validateResult = await useOrganizationStore.getState().validateLicenseKey(licenseKey);
            }

            if (validateResult?.resultType === 'Ok' && validateResult.data && validateResult.data?.isValid) {
                // upgrade
                if (organizationId) {
                    await activateExistingOrg();
                } else if (validateResult.data.maxMembers <= 1) {
                    const licenseType = getLicenseType(validateResult.data);
                    const description = t(`common:billing.${licenseType}.description`);
                    await activateNewOrg(DefaultOrgName, description);
                } else {
                    // proceed to next step
                    setCurrentStep(LicenseActivationSteps.OrganizationDetails);
                }
            } else {
                setErrorMessage(t('common:license.activate.invalid.key'));
                return;
            }
        }

        if (currentStep === LicenseActivationSteps.OrganizationDetails) {
            await activateNewOrg(orgName, orgDescription);
        }
    };

    /** UI State Management */
    const clearError = () => {
        if (errorMessage) {
            setErrorMessage(null);
        }
    };

    const onLicenseKeyChange = (e: any) => {
        clearError();
        setLicenseKey(e.target.value);
    };

    const onOrgNameChange = (e: any) => {
        clearError();
        setOrgName(e.target.value);
    };

    const onOrgDescriptionChange = (e: any) => {
        clearError();
        setOrgDescription(e.target.value);
    };

    const onDismiss = () => {
        setErrorMessage(null);
        setLicenseKey('');
        setOrgName('');
        setOrgDescription('');
        setCurrentStep(LicenseActivationSteps.LicenseKey);
        onCancel();
    };

    return (
        <ContentDialog
            title={t('common:license.activate.title')}
            description={description}
            open={open}
            onClose={onDismiss}
            onCancel={onDismiss}
            onConfirm={onActivate}
            confirmButtonText={t('common:license.activate.button')}>
            <SubSectionContent>
                {currentStep === LicenseActivationSteps.LicenseKey && (
                    <TextField
                        label={t('common:license.activate.fieldLabel')}
                        placeholder={t('common:license.activate.placeholder')}
                        required
                        value={licenseKey}
                        onChange={onLicenseKeyChange}
                        fullWidth
                    />
                )}
                {currentStep === LicenseActivationSteps.OrganizationDetails && (
                    <>
                        <TextField
                            label={t('common:organization.new.name.label')}
                            placeholder={t('common:organization.new.name.placeholder')}
                            required
                            value={orgName}
                            onChange={onOrgNameChange}
                            fullWidth
                        />
                        <TextField
                            label={t('common:organization.new.description.label')}
                            placeholder={t('common:organization.new.description.placeholder')}
                            value={orgDescription}
                            onChange={onOrgDescriptionChange}
                            fullWidth
                        />
                    </>
                )}
                {errorMessage && <ErrorText>{errorMessage}</ErrorText>}
            </SubSectionContent>
        </ContentDialog>
    );
}
